import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import {
  ActivityChart,
  BreakdownRow,
  WEB_STATS_BRAND,
  WebsiteMetricCard,
  actorLabel,
  categoryLabel,
  formatDate,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercent,
  safeNumber,
  sourceLabel,
} from "./WebsiteStatsUi";

const PRESETS = [
  { key: "today", label: "Hoy" },
  { key: "7days", label: "7 días" },
  { key: "30days", label: "30 días" },
  { key: "month", label: "Este mes" },
  { key: "custom", label: "Personalizado" },
];

function SectionTitle({ icon, title, description, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start" sx={{ mb: 2.2 }}>
      <Stack direction="row" spacing={1.2} alignItems="flex-start">
        <Box sx={{ color: WEB_STATS_BRAND.orange, display: "flex", mt: .15 }}>{icon}</Box>
        <Box>
          <Typography variant="h5" sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 950 }}>{title}</Typography>
          {description ? <Typography variant="body2" sx={{ color: WEB_STATS_BRAND.muted, mt: .3 }}>{description}</Typography> : null}
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

function InsightCard({ icon, label, value, helper }) {
  return (
    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: WEB_STATS_BRAND.background, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box sx={{ color: WEB_STATS_BRAND.orange, display: "flex" }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted, fontWeight: 900 }}>{label}</Typography>
          <Typography sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 950, fontSize: 19, lineHeight: 1.2 }}>{value}</Typography>
          {helper ? <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{helper}</Typography> : null}
        </Box>
      </Stack>
    </Box>
  );
}

function RecentEvent({ event }) {
  const severityColor = event.severity === "critical"
    ? "error"
    : event.severity === "warning"
      ? "warning"
      : event.severity === "success"
        ? "success"
        : "default";
  return (
    <Box sx={{ p: 1.55, borderRadius: 3, bgcolor: WEB_STATS_BRAND.background, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark }}>{event.title || event.action}</Typography>
          <Typography variant="body2" sx={{ color: WEB_STATS_BRAND.muted, mt: .25 }}>{event.description || "Movimiento registrado en el negocio."}</Typography>
          <Stack direction="row" spacing={.7} useFlexGap flexWrap="wrap" sx={{ mt: .9 }}>
            <Chip size="small" label={categoryLabel(event.category)} color={severityColor} />
            <Chip size="small" variant="outlined" label={sourceLabel(event.source)} />
            <Chip size="small" variant="outlined" label={actorLabel(event)} />
          </Stack>
        </Box>
        <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted, fontWeight: 800, flexShrink: 0 }}>{formatDateTime(event.occurredAt)}</Typography>
      </Stack>
    </Box>
  );
}

function friendlyPath(path = "/") {
  if (path === "/") return "Inicio de la web";
  if (path.includes("/dashboard/proyectos/") && path.endsWith("/actividad")) return "Historial de actividad";
  if (path.includes("/dashboard/proyectos/") && path.endsWith("/estadisticas")) return "Estadísticas";
  if (path.includes("/dashboard/proyectos/") && path.endsWith("/productos")) return "Productos y servicios";
  if (path.includes("/dashboard/proyectos/") && path.endsWith("/sistema")) return "Gestión del sistema";
  if (path.includes("/dashboard/proyectos/") && path.endsWith("/sitio")) return "Configuración de la web";
  return path;
}

export default function DesktopWebsiteStats({ analytics, range, onPreset, onRangeChange, onRefresh, loading, error }) {
  const totals = analytics?.totals || {};
  const comparisons = analytics?.comparisons || {};
  const categories = analytics?.categories || [];
  const sources = analytics?.sources || [];
  const actions = analytics?.actions || [];
  const team = analytics?.team || [];
  const paths = analytics?.paths || [];
  const recentEvents = analytics?.recentEvents || [];
  const insights = analytics?.insights || {};
  const totalEvents = Math.max(1, safeNumber(totals.events));
  const busiestHour = insights.busiestHour
    ? `${String(insights.busiestHour.hour).padStart(2, "0")}:00 a ${String((insights.busiestHour.hour + 1) % 24).padStart(2, "0")}:00`
    : "Sin horario dominante";

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" spacing={3} alignItems="flex-start">
        <Box>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: WEB_STATS_BRAND.orange }}><QueryStatsRoundedIcon /></Box>
            <Box>
              <Typography variant="h3" sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 950 }}>Actividad de tu negocio</Typography>
              <Typography sx={{ color: WEB_STATS_BRAND.muted, mt: .35, maxWidth: 900 }}>
                Una lectura general de cómo se mueve el negocio: operaciones, uso del panel, actividad de la web, equipo y recorridos registrados en el historial.
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Button onClick={onRefresh} disabled={loading} startIcon={<RefreshRoundedIcon />} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Actualizar</Button>
      </Stack>

      <Paper elevation={0} sx={{ p: 2.2, borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {PRESETS.map((preset) => (
            <Button
              key={preset.key}
              size="small"
              variant={range.preset === preset.key ? "contained" : "outlined"}
              onClick={() => onPreset(preset.key)}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: range.preset === preset.key ? WEB_STATS_BRAND.orange : undefined }}
            >
              {preset.label}
            </Button>
          ))}
          <Box sx={{ flex: 1 }} />
          <TextField size="small" type="date" label="Desde" value={range.dateFrom} onChange={(event) => onRangeChange("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
          <TextField size="small" type="date" label="Hasta" value={range.dateTo} onChange={(event) => onRangeChange("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}

      <Grid container spacing={2.2}>
        <Grid item xs={12} sm={6} lg={3}>
          <WebsiteMetricCard icon={<HistoryRoundedIcon />} label="Movimientos registrados" value={formatNumber(totals.events)} helper={`${formatNumber(totals.averagePerActiveDay, 1)} por día activo`} comparison={comparisons.events} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <WebsiteMetricCard icon={<BoltRoundedIcon />} label="Operaciones del negocio" value={formatNumber(totals.operations)} helper={`${formatPercent(insights.operationShare)} de toda la actividad`} comparison={comparisons.operations} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <WebsiteMetricCard icon={<PeopleAltRoundedIcon />} label="Personas activas" value={formatNumber(totals.activeUsers)} helper={`${formatNumber(totals.uniqueVisitors)} visitantes únicos en la web`} comparison={comparisons.activeUsers} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <WebsiteMetricCard icon={<RouteRoundedIcon />} label="Sesiones y recorridos" value={formatNumber(totals.sessions)} helper={`${formatNumber(totals.averageSessionSteps, 1)} pasos promedio`} comparison={comparisons.sessions} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<QueryStatsRoundedIcon />} title="Movimiento diario" description="Actividad total comparada con las operaciones concretas del negocio." />
            <ActivityChart rows={analytics?.daily || []} />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<AccessTimeRoundedIcon />} title="Lectura rápida" description="Datos útiles para entender el ritmo del período." />
            <Stack spacing={1.15}>
              <InsightCard icon={<HistoryRoundedIcon />} label="DÍAS CON ACTIVIDAD" value={`${formatNumber(totals.activeDays)} de ${formatNumber(analytics?.range?.days)}`} helper={totals.lastAt ? `Último movimiento: ${formatDateTime(totals.lastAt)}` : "Todavía sin movimientos"} />
              <InsightCard icon={<AccessTimeRoundedIcon />} label="HORARIO MÁS ACTIVO" value={busiestHour} helper={insights.busiestHour ? `${formatNumber(insights.busiestHour.count)} movimientos` : "Sin datos suficientes"} />
              <InsightCard icon={<BoltRoundedIcon />} label="DÍA CON MÁS MOVIMIENTO" value={insights.busiestDay ? formatDate(insights.busiestDay.date) : "Sin datos"} helper={insights.busiestDay ? `${formatNumber(insights.busiestDay.count)} movimientos` : "Sin datos suficientes"} />
              <InsightCard icon={<WarningAmberRoundedIcon />} label="ALERTAS REGISTRADAS" value={formatNumber(safeNumber(totals.warnings) + safeNumber(totals.critical))} helper={`${formatNumber(totals.success)} movimientos marcados como exitosos`} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<CategoryRoundedIcon />} title="Qué áreas se están moviendo" description="Distribución real del historial por parte del negocio." />
            <Stack spacing={1.6}>
              {categories.slice(0, 9).map((row) => (
                <BreakdownRow key={row.key} label={categoryLabel(row.key)} value={row.count} total={totalEvents} helper={`${formatPercent(row.percent)} del movimiento total`} />
              ))}
              {!categories.length ? <Alert severity="info">Todavía no hay áreas con actividad en este período.</Alert> : null}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<DashboardRoundedIcon />} title="De dónde viene la actividad" description="Panel, web pública, procesos e integraciones." />
            <Stack spacing={1.6}>
              {sources.map((row, index) => (
                <BreakdownRow
                  key={row.key}
                  label={sourceLabel(row.key)}
                  value={row.count}
                  total={totalEvents}
                  helper={`${formatPercent(row.percent)} del total`}
                  color={[WEB_STATS_BRAND.orange, WEB_STATS_BRAND.blue, WEB_STATS_BRAND.purple, WEB_STATS_BRAND.green][index % 4]}
                />
              ))}
              {!sources.length ? <Alert severity="info">No hay orígenes de actividad para mostrar.</Alert> : null}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<TouchAppRoundedIcon />} title="Acciones más repetidas" description="Qué está ocurriendo con mayor frecuencia dentro del negocio." />
            {!actions.length ? (
              <Alert severity="info">No hay acciones registradas en este período.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 950 }}>Acción</TableCell>
                      <TableCell sx={{ fontWeight: 950 }}>Área</TableCell>
                      <TableCell sx={{ fontWeight: 950 }}>Origen</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 950 }}>Veces</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actions.slice(0, 12).map((row) => (
                      <TableRow key={`${row.action}-${row.category}-${row.source}`} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark }}>{row.label}</Typography>
                          <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>Última: {formatDateTime(row.lastAt)}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={categoryLabel(row.category)} /></TableCell>
                        <TableCell>{sourceLabel(row.source)}</TableCell>
                        <TableCell align="right"><Chip size="small" label={formatNumber(row.count)} sx={{ fontWeight: 950 }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<PeopleAltRoundedIcon />} title="Actividad del equipo" description="Quiénes estuvieron usando y operando el sistema." />
            <Stack spacing={1.05}>
              {team.slice(0, 10).map((person, index) => (
                <Stack key={person.userId || `${person.email}-${index}`} direction="row" spacing={1.2} alignItems="center" sx={{ p: 1.25, borderRadius: 2.5, bgcolor: WEB_STATS_BRAND.background }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: 2.2, bgcolor: "rgba(243,90,58,.10)", color: WEB_STATS_BRAND.orange, display: "grid", placeItems: "center", fontWeight: 950 }}>{index + 1}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark }}>{person.name || person.email || "Usuario"}</Typography>
                    <Typography noWrap variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{person.role || "Miembro"} · Último: {formatDateTime(person.lastAt)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontWeight: 950, color: WEB_STATS_BRAND.dark }}>{formatNumber(person.events)}</Typography>
                    <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{formatNumber(person.operations)} operaciones</Typography>
                  </Box>
                </Stack>
              ))}
              {!team.length ? <Alert severity="info">Todavía no hay usuarios del panel activos en este período.</Alert> : null}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<RouteRoundedIcon />} title="Secciones con más movimiento" description="Rutas del panel y de la web que concentraron actividad." />
            <Stack spacing={1.05}>
              {paths.slice(0, 10).map((row, index) => (
                <Stack key={`${row.channel}-${row.path}`} direction="row" spacing={1.1} alignItems="center" sx={{ p: 1.2, borderRadius: 2.5, bgcolor: WEB_STATS_BRAND.background }}>
                  <Box sx={{ color: row.channel === "storefront" ? WEB_STATS_BRAND.blue : WEB_STATS_BRAND.orange, display: "flex" }}>
                    {row.channel === "storefront" ? <StorefrontRoundedIcon /> : <DashboardRoundedIcon />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark }}>{friendlyPath(row.path)}</Typography>
                    <Typography noWrap variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{sourceLabel(row.channel)}{row.averageDurationMs ? ` · ${formatDuration(row.averageDurationMs)} promedio` : ""}</Typography>
                  </Box>
                  <Chip size="small" label={formatNumber(row.count)} sx={{ fontWeight: 950 }} />
                </Stack>
              ))}
              {!paths.length ? <Alert severity="info">Todavía no hay recorridos suficientes para mostrar.</Alert> : null}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={{ p: 2.6, height: "100%", borderRadius: 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
            <SectionTitle icon={<HistoryRoundedIcon />} title="Últimos movimientos" description="Actividad reciente tomada directamente del historial del negocio." />
            <Stack spacing={1.05}>
              {recentEvents.slice(0, 12).map((event) => <RecentEvent key={event.id} event={event} />)}
              {!recentEvents.length ? <Alert severity="info">No hubo movimientos durante este período.</Alert> : null}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}
